import { FeedingForm } from 'components/FeedingForm/FeedingForm';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { MedicationForm } from 'components/MedicationForm/MedicationForm';
import { PetChip } from 'components/Pet/Chip/PetChip';
import { useMarketplace } from 'hooks/useMarketplace';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import {
    filterAddonsByBusinessAreas,
    getActiveAddonServices
} from 'reducers/addonServices/AddonServicesSelectors';
import { MarketplaceServiceCategory } from 'types/Marketplace';
import { dispatch } from 'store';
import {
    navigateToStep,
    updateOnlineBookingBookingItem
} from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import { OnlineBookingFooter } from 'components/OnlineBooking/Footer/OnlineBookingFooter';
import { OnlineBookingHeader } from 'components/OnlineBooking/Header/OnlineBookingHeader';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import { getOnlineBookingBoardingItems } from 'reducers/onlineBooking/OnlineBookingSelectors';
import { Feeding, Medication, OnlineBookingStep } from 'types/OnlineBooking';

interface OnlineBookingInstructionsFormData {
    feedingsByPet: Record<string, Array<Feeding>>;
    medicationsByPet: Record<string, Array<Medication>>;
}

export const OnlineBookingBoardingInstructionsStep: React.FC = () => {
    const marketplace = useMarketplace();

    const addonServices = filterAddonsByBusinessAreas(useSelector(getActiveAddonServices), [
        MarketplaceServiceCategory.BOARDING
    ]);
    const bookingItems = useSelector(getOnlineBookingBoardingItems);

    const [selectedBookingItemUuid, setSelectedBookingItemUuid] = React.useState<string>(
        bookingItems[0].uuid
    );

    const selectedBookingItem = React.useMemo(() => {
        return bookingItems.find(item => item.uuid === selectedBookingItemUuid);
    }, [bookingItems, selectedBookingItemUuid]);

    // Initialize form with all pets' data
    const initialFeedingsByPet = React.useMemo(() => {
        return bookingItems.reduce(
            (acc, item) => {
                acc[item.uuid] = item.feedings || [];
                return acc;
            },
            {} as Record<string, Array<Feeding>>
        );
    }, [bookingItems]);

    const initialMedicationsByPet = React.useMemo(() => {
        return bookingItems.reduce(
            (acc, item) => {
                acc[item.uuid] = item.medications || [];
                return acc;
            },
            {} as Record<string, Array<Medication>>
        );
    }, [bookingItems]);

    const {
        formState: { isValid },
        setValue,
        watch
    } = useForm<OnlineBookingInstructionsFormData>({
        defaultValues: {
            feedingsByPet: initialFeedingsByPet,
            medicationsByPet: initialMedicationsByPet
        },
        mode: 'onChange'
    });

    const feedingsByPet = watch('feedingsByPet');
    const medicationsByPet = watch('medicationsByPet');

    // Get current pet's data
    const currentFeedings = feedingsByPet[selectedBookingItemUuid] || [];
    const currentMedications = medicationsByPet[selectedBookingItemUuid] || [];

    const handleFeedingsChange = React.useCallback(
        (newFeedings: Array<Feeding>) => {
            setValue(`feedingsByPet.${selectedBookingItemUuid}`, newFeedings, {
                shouldValidate: true
            });
            dispatch(
                updateOnlineBookingBookingItem(selectedBookingItemUuid, {
                    feedings: newFeedings
                })
            );
        },
        [selectedBookingItemUuid, setValue]
    );

    const handleMedicationsChange = React.useCallback(
        (newMedications: Array<Medication>) => {
            setValue(`medicationsByPet.${selectedBookingItemUuid}`, newMedications, {
                shouldValidate: true
            });
            dispatch(
                updateOnlineBookingBookingItem(selectedBookingItemUuid, {
                    medications: newMedications
                })
            );
        },
        [selectedBookingItemUuid, setValue]
    );

    const backHandler = () => {
        if (addonServices.length > 0) {
            return dispatch(navigateToStep(OnlineBookingStep.AddonList));
        }

        dispatch(navigateToStep(OnlineBookingStep.TimePicker));
    };

    const submitHandler = () => {
        if (!isValid) {
            return;
        }
        dispatch(navigateToStep(OnlineBookingStep.ServiceCategory));
    };

    // Validate feedings for ALL pets (required for boarding - each pet needs at least one valid feeding)
    const allFeedingsValid = Object.values(feedingsByPet).every(
        feedings =>
            feedings &&
            feedings.length > 0 &&
            feedings.every(
                feeding =>
                    feeding.momentTypes.length > 0 &&
                    feeding.quantity.quantity > 0 &&
                    feeding.foodProvisionType
            )
    );

    // Validate medications for ALL pets (optional, but must be valid if present)
    const allMedicationsValid = Object.values(medicationsByPet).every(
        medications =>
            !medications ||
            medications.length === 0 ||
            medications.every(
                medication =>
                    medication.name &&
                    medication.name.trim().length > 0 &&
                    medication.dosage.quantity > 0 &&
                    medication.schedule.length > 0
            )
    );

    const canContinue = allFeedingsValid && allMedicationsValid;

    return (
        <TwoColumnsLayout header={<OnlineBookingHeader onBack={backHandler} />}>
            <LeftColumn>
                <h1 className="h1 underlined w-full mb-5">Feeding & Medication</h1>

                <div className="flex gap-3 mb-6 no-scrollbar">
                    {bookingItems.map(item => (
                        <PetChip
                            id={item.pet.uuid}
                            key={item.pet.uuid}
                            petName={item.pet.name}
                            petType={item.pet.type}
                            active={selectedBookingItemUuid === item.uuid}
                            onClick={() => setSelectedBookingItemUuid(item.uuid)}
                        />
                    ))}
                </div>

                <div className="flex flex-col gap-8">
                    <FeedingForm
                        key={`feeding-${selectedBookingItemUuid}`}
                        required
                        value={currentFeedings}
                        onChange={handleFeedingsChange}
                        bookingStartDate={selectedBookingItem?.startDate}
                        bookingEndDate={selectedBookingItem?.endDate}
                    />

                    <MedicationForm
                        key={`medication-${selectedBookingItemUuid}`}
                        value={currentMedications}
                        onChange={handleMedicationsChange}
                        bookingStartDate={selectedBookingItem?.startDate}
                        bookingEndDate={selectedBookingItem?.endDate}
                        timeZone={marketplace.timeZone}
                    />
                </div>
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={
                    <OnlineBookingFooter
                        label="Continue"
                        onSubmit={submitHandler}
                        disabled={!canContinue}
                    />
                }
            >
                <div className="flex flex-col gap-5 hide-on-mobile-and-tablet">
                    <MarketplaceInfo inverted marketplace={marketplace} />

                    <OnlineBookingResume />
                </div>
            </RightColumn>
        </TwoColumnsLayout>
    );
};
